import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";

/**
 * Reads the `highlight` search param from the current route and, when it
 * matches `name`, scrolls the referenced element into view and applies an
 * orbiting border animation. Dismisses when the user focuses within.
 */
export function useHighlight(name: string) {
	const searchParams = useSearch({
		from: "/_app/apps/$appId/",
		shouldThrow: false,
	});
	const navigate = useNavigate({ from: "/apps/$appId/" });
	const ref = useRef<HTMLDivElement>(null);
	const isHighlighted = searchParams?.highlight === name;

	const dismiss = useCallback(() => {
		navigate({
			search: (prev) => {
				const { highlight: _, ...rest } = prev;
				return rest;
			},
			replace: true,
		});
	}, [navigate]);

	useEffect(() => {
		if (!isHighlighted || !ref.current) return;

		// Small delay so the page settles before scrolling
		const scrollTimer = setTimeout(() => {
			ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 300);

		// Dismiss on focus-within (user clicked/tabbed into the section)
		const el = ref.current;
		el.addEventListener("focusin", dismiss);

		return () => {
			clearTimeout(scrollTimer);
			el.removeEventListener("focusin", dismiss);
		};
	}, [isHighlighted, dismiss]);

	return { ref, isHighlighted };
}
